import {
  type DeployEndpointRow,
  fetchCertificateById,
  fetchEndpoints,
  getEndpointUrl,
  requiresAcmeSetup,
  selectAppById,
  selectDatabaseById,
  selectEndpointsByAppIdForTable,
  selectEndpointsByCertIdForTable,
  selectEndpointsByDbIdForTable,
  selectEndpointsByEnvIdForTable,
  selectEndpointsByServiceIdForTable,
  selectEndpointsForTable,
} from "@app/deploy";
import { useCompositeLoader, useQuery, useSelector } from "@app/react";
import {
  appEndpointCreateUrl,
  databaseEndpointCreateUrl,
  endpointDetailSetupUrl,
  endpointDetailUrl,
} from "@app/routes";
import type { DeployEndpoint } from "@app/types";
import { useNavigate } from "react-router";
import { Link } from "react-router-dom";
import { Button, ButtonCreate } from "../button";
import { CopyTextButton } from "../copy";
import { costColDef } from "../cost-estimate-tooltip";
import { DataTable, idColDef } from "../datatable";
import {
  CurrencyCell,
  DateTimeCell,
  EndpointCell,
  EnvironmentCell,
  ServiceCell,
} from "../datatable/cells";
import type {
  ColDef,
  DataTableCellRendererProps,
  DataTableColDef,
} from "../datatable/datatable";
import { Group } from "../group";
import { IconPlusCircle } from "../icons";
import { TitleBar } from "../resource-list-view";
import { tokens } from "../tokens";
import { EndpointStatusDisplay, SharedEndpointCell, isWildcard } from "./util";

export const EndpointItemView = ({
  endpoint,
}: { endpoint: DeployEndpoint }) => {
  return (
    <Link
      className="flex items-center gap-1 text-black group-hover:text-indigo hover:text-indigo"
      to={endpointDetailUrl(endpoint.id)}
    >
      <img
        src="/resource-types/logo-vhost.png"
        className="w-[32px] h-[32px] mr-2 align-middle"
        aria-label="Endpoint"
      />
      <p className={`${tokens.type["table link"]} leading-8`}>
        {getEndpointUrl(endpoint)}
      </p>
    </Link>
  );
};

// ─── Shared column definitions ────────────────────────────────────────────────

const colDefs = {
  url: {
    field: "url",
    headerName: "URL",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployEndpointRow>) => (
      <EndpointCell id={data.id} />
    ),
  },
  serviceData: {
    colId: "serviceData",
    sortable: true,
    width: 350,
    resizable: true,
    suppressAutoSize: false,
    suppressSizeToFit: false,
    headerName: "Resource",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployEndpointRow>) => (
      <ServiceCell id={data.serviceId} />
    ),
    // AG Grid types data as T | undefined in ValueGetterParams to account for
    // non-data row types (group rows, pinned rows) where data does not exist
    valueGetter: ({ data }: { data?: DeployEndpointRow }) =>
      data ? `${data.resourceHandle} ${data.serviceProcessType}` : "",
  },
  id: {
    field: "id",
    initialHide: true,
    ...idColDef,
  },
  hostname: {
    field: "hostname",
    headerName: "Hostname",
    width: 400,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployEndpointRow>) => (
      <div className="flex">
        <span>{data.hostname}</span>
        <CopyTextButton text={data.hostname} className="ml-2" />
      </div>
    ),
  },
  environment: {
    field: "environmentId",
    headerName: "Environment",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployEndpointRow>) => (
      <EnvironmentCell id={data.environmentId} hideIcon={true} />
    ),
  },
  type: {
    field: "type",
    headerName: "Type",
  },
  status: {
    field: "status",
    headerName: "Status",
    initialHide: true,
    minWidth: 150,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployEndpointRow>) => (
      <EndpointStatusDisplay status={data.status} />
    ),
  },
  placement: {
    field: "placement",
    headerName: "Placement",
  },
  platform: {
    field: "platform",
    headerName: "Platform",
    initialHide: true,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployEndpointRow>) => (
      <span>{data.platform.toUpperCase()}</span>
    ),
  },
  shared: {
    field: "shared",
    headerName: "Shared",
    headerTooltip:
      "Shared endpoints share a load balancer across apps. When enabled, Aptible gives you a reduced per-endpoint rate. Highly recommended for non-production resources. Only available for HTTP endpoints on ALB.",
    valueGetter: ({ data }: { data?: DeployEndpointRow }) => {
      if (!data || data.platform !== "alb" || isWildcard(getEndpointUrl(data)))
        return "N/A";
      return data.shared ? "Enabled" : "Available";
    },
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployEndpointRow>) => (
      <SharedEndpointCell enp={data} />
    ),
  },
  acme: {
    field: "acmeStatus",
    headerName: "ACME Status",
    initialHide: true,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployEndpointRow>) => {
      const navigate = useNavigate();
      if (requiresAcmeSetup(data)) {
        return (
          <Button
            variant="primary"
            size="sm"
            onClick={() => navigate(endpointDetailSetupUrl(data.id))}
          >
            Setup
          </Button>
        );
      }
      return <span>Completed</span>;
    },
  },
  createdAt: {
    field: "createdAt",
    headerName: "Created At (UTC)",
    initialSort: "desc",
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployEndpointRow>) => (
      <DateTimeCell date={data.createdAt} />
    ),
  },
  cost: {
    field: "cost",
    ...costColDef,
    cellRenderer: ({ data }: DataTableCellRendererProps<DeployEndpointRow>) => (
      <CurrencyCell value={data.cost} />
    ),
  },
} satisfies Record<string, DataTableColDef<DeployEndpointRow>>;

// ─── Exported components ──────────────────────────────────────────────────────

const endpointsByOrgColDefs = [
  colDefs.url,
  colDefs.serviceData,
  colDefs.environment,
  colDefs.hostname,
  colDefs.type,
  colDefs.status,
  colDefs.placement,
  colDefs.platform,
  colDefs.shared,
  colDefs.acme,
  colDefs.createdAt,
  colDefs.cost,
];

export function EndpointsByOrg() {
  const { isLoading, isInitialLoading } = useCompositeLoader([fetchEndpoints]);
  const endpoints = useSelector((s) => selectEndpointsForTable(s));

  return (
    <Group size="xl">
      <TitleBar description="Endpoints let you expose your Apps on Aptible to clients over the public internet or your Stack's internal network.">
        Endpoints
      </TitleBar>
      <DataTable<DeployEndpointRow>
        rowData={endpoints}
        colDefs={endpointsByOrgColDefs}
        isLoading={!endpoints.length && (isLoading || isInitialLoading)}
      />
    </Group>
  );
}

const endpointsByEnvColDefs: ColDef<DeployEndpointRow>[] = [
  colDefs.url,
  colDefs.serviceData,
  colDefs.hostname,
  colDefs.type,
  colDefs.status,
  colDefs.placement,
  colDefs.platform,
  colDefs.shared,
  colDefs.acme,
  colDefs.createdAt,
  colDefs.cost,
];

export function EndpointsByEnv({ envId }: { envId: string }) {
  const endpoints = useSelector((s) =>
    selectEndpointsByEnvIdForTable(s, { envId }),
  );

  return (
    <DataTable<DeployEndpointRow>
      rowData={endpoints}
      colDefs={endpointsByEnvColDefs}
    />
  );
}

const endpointsByAppColDefs: ColDef<DeployEndpointRow>[] = [
  colDefs.url,
  colDefs.serviceData,
  colDefs.hostname,
  colDefs.status,
  colDefs.placement,
  colDefs.platform,
  colDefs.shared,
  colDefs.acme,
  colDefs.createdAt,
  colDefs.cost,
];

export function EndpointsByApp({ appId }: { appId: string }) {
  const navigate = useNavigate();
  const app = useSelector((s) => selectAppById(s, { id: appId }));
  const endpoints = useSelector((s) =>
    selectEndpointsByAppIdForTable(s, { appId }),
  );

  return (
    <Group size="xl">
      <div className="flex justify-end">
        <ButtonCreate
          envId={app.environmentId}
          onClick={() => navigate(appEndpointCreateUrl(app.id))}
        >
          <IconPlusCircle variant="sm" className="mr-2" /> New Endpoint
        </ButtonCreate>
      </div>
      <DataTable<DeployEndpointRow>
        rowData={endpoints}
        colDefs={endpointsByAppColDefs}
      />
    </Group>
  );
}

const endpointsByDatabaseColDefs = [
  colDefs.url,
  colDefs.serviceData,
  colDefs.hostname,
  colDefs.status,
  colDefs.placement,
  colDefs.platform,
  colDefs.shared,
  colDefs.acme,
  colDefs.createdAt,
  colDefs.cost,
];

export function EndpointsByDatabase({ dbId }: { dbId: string }) {
  const navigate = useNavigate();
  const db = useSelector((s) => selectDatabaseById(s, { id: dbId }));
  const endpoints = useSelector((s) =>
    selectEndpointsByDbIdForTable(s, { dbId }),
  );

  return (
    <Group size="xl">
      <div className="flex justify-end">
        <ButtonCreate
          envId={db.environmentId}
          onClick={() => navigate(databaseEndpointCreateUrl(dbId))}
        >
          <IconPlusCircle variant="sm" className="mr-2" /> New Endpoint
        </ButtonCreate>
      </div>
      <DataTable<DeployEndpointRow>
        rowData={endpoints}
        colDefs={endpointsByDatabaseColDefs}
      />
    </Group>
  );
}

const endpointsByCertColDefs = [
  colDefs.url,
  colDefs.serviceData,
  colDefs.hostname,
  colDefs.status,
  colDefs.placement,
  colDefs.platform,
  colDefs.shared,
  colDefs.acme,
  colDefs.createdAt,
  colDefs.cost,
];

export function EndpointsByCert({ certId }: { certId: string }) {
  useQuery(fetchCertificateById({ certId }));
  const endpoints = useSelector((s) =>
    selectEndpointsByCertIdForTable(s, { certId }),
  );

  return (
    <DataTable<DeployEndpointRow>
      rowData={endpoints}
      colDefs={endpointsByCertColDefs}
    />
  );
}

const endpointsByDbServiceColDefs = [
  colDefs.url,
  colDefs.serviceData,
  colDefs.hostname,
  colDefs.status,
  colDefs.placement,
  colDefs.platform,
  colDefs.shared,
  colDefs.acme,
  colDefs.createdAt,
  colDefs.cost,
];

export function EndpointsByDbService({
  serviceId,
  dbId,
}: { serviceId: string; dbId: string }) {
  const navigate = useNavigate();
  const db = useSelector((s) => selectDatabaseById(s, { id: dbId }));
  const endpoints = useSelector((s) =>
    selectEndpointsByServiceIdForTable(s, { serviceId }),
  );

  return (
    <Group size="xl">
      <div className="flex justify-end">
        <ButtonCreate
          envId={db.environmentId}
          onClick={() => navigate(databaseEndpointCreateUrl(dbId))}
        >
          <IconPlusCircle variant="sm" className="mr-2" /> New Endpoint
        </ButtonCreate>
      </div>
      <DataTable<DeployEndpointRow>
        rowData={endpoints}
        colDefs={endpointsByDbServiceColDefs}
      />
    </Group>
  );
}
